/**
 * The minimum wage table (spec 019 D26), platform owner. Mirrors the backend
 * `upsertMinimumWageRate` checks so mistakes show before the call.
 */

export interface WageForm {
  state: string;
  city: string;
  hourlyRate: string;
  effectiveFrom: string;
  source: string;
}

export type WageInput = {
  state: string;
  city: string | null;
  hourlyRate: number;
  effectiveFrom: string;
  source: string;
};

export function wageInput(form: WageForm): { input: WageInput | null; problem: string | null } {
  const state = form.state.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(state)) return { input: null, problem: 'Use the two-letter state code' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.effectiveFrom.trim())) {
    return { input: null, problem: 'Pick the date the wage starts' };
  }
  const rate = Number(form.hourlyRate);
  if (!form.hourlyRate.trim() || !(rate >= 7.25 && rate <= 100)) {
    return { input: null, problem: 'The wage is from $7.25 to $100 an hour' };
  }
  const source = form.source.trim();
  if (!source) return { input: null, problem: 'Name the source, for example "US DOL 2026-01"' };
  return {
    input: {
      state,
      city: form.city.trim() || null,
      hourlyRate: Math.round(rate * 100) / 100,
      effectiveFrom: form.effectiveFrom.trim(),
      source,
    },
    problem: null,
  };
}

/** The rate in force on `day` (yyyy-MM-dd) for each state and city: the latest start not after it. */
export function currentRateIds(
  rows: readonly { id: string; state: string; city?: string | null; effectiveFrom: string }[],
  day: string,
): Set<string> {
  const best = new Map<string, { id: string; effectiveFrom: string }>();
  for (const row of rows) {
    if (row.effectiveFrom > day) continue;
    const key = `${row.state}:${row.city ?? ''}`;
    const seen = best.get(key);
    if (!seen || row.effectiveFrom > seen.effectiveFrom) best.set(key, row);
  }
  return new Set(Array.from(best.values()).map(r => r.id));
}
